import type { ColumnsType } from 'antd/es/table'
import type { RenderExpandIcon } from 'rc-table/lib/interface'
import type { ClassAttributes, HTMLAttributes } from 'react'
import { Fragment, useCallback, useEffect, useMemo, useReducer, useState } from 'react'
import type {
  BatchSelectionContextValue,
  CheckRunsResponse,
  DeleteCheckRunsMutationVariables,
  EobMetaResponse,
  PartialCheckRun,
  RefreshCheckRunCacheMutationVariables,
  Response,
  RestoreCheckRunMutationVariables,
  State,
  VoidCheckRunMutationVariables
} from './types'

import {
  CalendarOutlined,
  CaretDownOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  CloudUploadOutlined,
  DeleteFilled,
  DollarCircleOutlined,
  EllipsisOutlined,
  FileExcelOutlined,
  FireOutlined,
  FlagFilled,
  HistoryOutlined,
  InfoCircleOutlined,
  PauseCircleFilled,
  ReloadOutlined,
  RollbackOutlined,
  SearchOutlined,
  SyncOutlined,
  ToolOutlined
} from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert,
  Button,
  Checkbox,
  Dropdown,
  Input,
  InputNumber,
  Modal,
  notification,
  Space,
  Table,
  Tag,
  Tooltip
} from 'antd'
import { isAxiosError } from 'axios'
import cx from 'classnames'
import { endOfDay, format, formatISO, isValid as isValidDate, parseISO, startOfDay } from 'date-fns'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useDebounce, useInterval } from 'react-use'

import $api from '../../../API'
import { Role } from '../../../domain/role.enum'
import { useAppSelector } from '../../../store'
import { DateFilterDropdown } from '../../notifications'
import type { HelpSection } from '../../../components/page'
import {
  PageContainer,
  PageHeader,
  HelpDrawerLayout,
  HelpDrawerTrigger
} from '../../../components/page'
import { LiveUpdatesControl } from '../../../components/LiveUpdatesControl'
import { useCheckRunEvents } from '../../../lib/socket/use-check-run-events'

import './index.css'

import DebounceSelect from '../../../components/filterComponent/Filter'
import Payments from '../../../API/payments'

import { BatchActionButton } from './components/batch-action-button'
import { BatchSelectionPanel } from './components/batch-selection-panel'
import { DropoffModal } from './components/dropoff-modal'
import { EmptyCellValue } from './components/empty-cell-value'
import { ExpandIcon } from './components/expand-icon'
import { FilePicker } from './components/file-picker'
import { HumanReadableTime } from './components/human-readable-time'
import { MiniDashboard } from './components/mini-dashboard'
import { releaseTypeFilterOptions } from './components/release-type-filter-options'
import { RerunCheckRunModal } from './components/rerun-check-run-modal'
import {
  baseCellClassName,
  baseHeaderCellClassName,
  BatchSelectionContext,
  releaseTypeMapper,
  statusGroups,
  statusMapper,
  weekDays
} from './constants'
import {
  currencyFormatter,
  humanizeDuration,
  pluralize,
  round,
  syncFiltersToUrlParams,
  uniquePredicate
} from './functions'
import { initialState, reducer } from './store'
import { CheckRunExpandableRow } from './components/check-run-expandable-row'
import { Duplicate } from '../../../components/Icons/Duplicate/Duplicate'

export const currency = (value: number): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(value)

const releasePaymentFilesHelpSections: HelpSection[] = [
  {
    content: (
      <p>
        Manage and release payment files (check runs) across all groups. Track statuses, apply
        filters, and perform batch operations.
      </p>
    ),
    dividerAfter: true
  },
  {
    title: 'Refresh / Sync',
    content: (
      <ul className='list-disc pl-4 space-y-1'>
        <li>Auto-refresh: 30s / 60s / 5min / 30min.</li>
        <li>Live Updates: real-time via WebSocket.</li>
        <li>EOB sync status shown in table footer.</li>
      </ul>
    )
  },
  {
    title: 'Responsibilities',
    content: <p>Operations and finance teams. Admins: batch rerun, void, restore.</p>
  },
  {
    title: 'Release / Version',
    content: <p>Core page since initial release.</p>,
    dividerAfter: true
  },
  {
    title: 'Columns',
    content: (
      <ul className='list-disc pl-4 space-y-1'>
        <li>
          <strong>Group # / Name</strong> — Identifier and org name.
        </li>
        <li>
          <strong>Location</strong> — Short code. Multi-select filter.
        </li>
        <li>
          <strong>File Name / Date</strong> — Payment file info.
        </li>
        <li>
          <strong>Amount</strong> — Total. Exact, zero, non-zero, empty filters.
        </li>
        <li>
          <strong>Release Type</strong> — Auto, Email, Deposit, or Level-Funded.
        </li>
        <li>
          <strong>Scheduled Release</strong> — Next date or waiting status.
        </li>
        <li>
          <strong>Status</strong> — Color-coded tag. &ldquo;Mixed&rdquo; for multiple.
        </li>
        <li>
          <strong>Flags</strong> — On Hold, Stop Loss, Duplicates, Mismatch, Voided, Network,
          Deleted.
        </li>
      </ul>
    ),
    dividerAfter: true
  },
  {
    title: 'Available Actions',
    content: (
      <>
        <p className='mb-1 font-medium'>Row</p>
        <ul className='list-disc pl-4 space-y-0.5'>
          <li>Rerun — re-execute workflow.</li>
          <li>Void — void $0 runs with no success status.</li>
          <li>Restore — undelete (Admin).</li>
          <li>Refresh Cache — reload cached data.</li>
          <li>Find in Scheduler — jump to group schedule.</li>
          <li>Dropoff to LIN — when single released status.</li>
        </ul>
        <p className='mt-2 mb-1 font-medium'>Batch (select rows)</p>
        <ul className='list-disc pl-4 space-y-0.5'>
          <li>Delete — runs with no payment files.</li>
          <li>Rerun / Void — Admin only.</li>
        </ul>
      </>
    ),
    dividerAfter: true
  },
  {
    title: 'Workflow',
    content: (
      <ol className='list-decimal pl-4 space-y-0.5'>
        <li>Use dashboard shortcuts to filter by status.</li>
        <li>Review check runs; expand rows for details.</li>
        <li>Narrow with column filters or search bar.</li>
        <li>Act on rows (rerun, void, dropoff) or select for batch.</li>
        <li>Enable Live Updates or auto-refresh for monitoring.</li>
      </ol>
    )
  }
]

export function ReleasePaymentFilesPage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams()
  const [isFilePickerOpen, setIsFilePickerOpen] = useState(false)

  const [state, dispatch] = useReducer(reducer, initialState, state => {
    if (searchParams.get('quick-filter') === 'incomplete') {
      state = {
        ...state,
        filters: {
          ...state.filters,
          statusIds: statusGroups
            .flatMap(group => group.statusIds)
            .filter(id => !['5', '6', '8'].includes(id))
        }
      }
    }

    return state
  })
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(state.searchQuery)

  const user = useAppSelector(state => state.app.user)

  const has = useCallback(
    (roles: Role[]): boolean => {
      return roles.some(role => user.roles.includes(role))
    },
    [user.roles]
  )

  useDebounce(
    () => {
      setDebouncedSearchQuery(state.searchQuery.toLowerCase().trim())
    },
    500,
    [state.searchQuery]
  )
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // Real-time updates via WebSocket
  const { isConnected } = useCheckRunEvents({ enabled: state.liveUpdatesEnabled, showToasts: true })

  const validStatusIds = new Set([
    ...statusGroups.flatMap(group => group.statusIds),
    'mixed',
    'no-payment-file',
    'deleted'
  ])

  const query = useQuery({
    queryKey: [
      'release-payment-files',
      {
        searchQuery: debouncedSearchQuery,
        filters: {
          ...state.filters,
          statusIds: state.filters.statusIds.filter(id => validStatusIds.has(id))
        },
        currentPage: state.pagination.currentPage,
        pageSize: state.pagination.pageSize
      }
    ],

    async queryFn({ queryKey }) {
      const { searchQuery, filters, currentPage, pageSize } =
        queryKey[1] as typeof queryKey extends Array<string | infer T> ? T : never

      const fileDateFrom =
        typeof filters.date.from === 'string' && filters.date.from.length > 0
          ? parseISO(filters.date.from)
          : null
      const fileDateTo =
        typeof filters.date.to === 'string' && filters.date.to.length > 0
          ? parseISO(filters.date.to)
          : null

      const scheduledReleaseDateFrom =
        typeof filters.scheduledReleaseDate.from === 'string' &&
        filters.scheduledReleaseDate.from.length > 0
          ? parseISO(filters.scheduledReleaseDate.from)
          : null
      const scheduledReleaseDateTo =
        typeof filters.scheduledReleaseDate.to === 'string' &&
        filters.scheduledReleaseDate.to.length > 0
          ? parseISO(filters.scheduledReleaseDate.to)
          : null

      const searchParams = new URLSearchParams([
        ['page', currentPage.toString()],
        ['page-size', pageSize.toString()],
        ...(searchQuery.length > 0 ? [['search', searchQuery]] : []),
        ...filters.statusIds.map(id => ['status-id', id]),
        ...filters.locationIds.map(id => ['location-id', id]),
        ...(fileDateFrom && isValidDate(fileDateFrom)
          ? [['file-date.from', formatISO(fileDateFrom, { representation: 'date' })]]
          : []),
        ...(fileDateTo && isValidDate(fileDateTo)
          ? [['file-date.to', formatISO(fileDateTo, { representation: 'date' })]]
          : []),
        ...(scheduledReleaseDateFrom && isValidDate(scheduledReleaseDateFrom)
          ? [['scheduled-release.from', formatISO(startOfDay(scheduledReleaseDateFrom))]]
          : []),
        ...(scheduledReleaseDateTo && isValidDate(scheduledReleaseDateTo)
          ? [['scheduled-release.to', formatISO(endOfDay(scheduledReleaseDateTo))]]
          : []),
        ...(state.filters.groupNumber ? [['group-number', state.filters.groupNumber]] : []),
        ...filters.flags.map(flag => {
          if (flag === 'network') {
            return ['network', 'network']
          }
          return ['flag', flag]
        }),
        ...filters.releaseType.map(releaseType => ['release-type', releaseType]),
        ...filters.amount.map(amount => ['amount', amount])
      ])

      const { data } = await $api.get<CheckRunsResponse>(
        `/payments/release?${searchParams.toString()}`
      )

      return data
    },

    keepPreviousData: true
  })

  const filtersQuery = useQuery({
    queryKey: ['check-runs', 'meta', 'filters'],

    async queryFn({ signal }) {
      const response = await $api.get<Response>('/check-runs/meta/filters', {
        signal
      })

      return response.data
    }
  })

  useEffect(() => {
    const ids = query.data?.data?.map(run => run.id) ?? []
    const filtered = state.selectedCheckRunIds.filter(id => ids.includes(id))

    if (filtered.length === state.selectedCheckRunIds.length) {
      return
    }

    dispatch({
      type: 'checkRunIds/select',
      payload: filtered
    })
  }, [query.data, state.selectedCheckRunIds])

  // Commented out - not currently used
  // const _rebuildCheckRunMutation = useMutation({
  //   async mutationFn(variables: RebuildCheckRunMutationVariables) {
  //     try {
  //       const response = await $api.post(`/check-runs/${variables.id}/rebuild`, {})

  //       if (response.status === 204) {
  //         return
  //       }

  //       return response.data
  //     } catch (e) {
  //       if (isAxiosError(e)) {
  //         console.info(e)
  //         const err = new Error(e.response?.data?.message ?? 'Failed to rebuild check run')

  //         // @ts-expect-error // it's okay
  //         err.description = e.response?.data?.description
  //         throw err
  //       }
  //     }
  //   }
  // })

  const voidCheckRunMutation = useMutation({
    async mutationFn(variables: VoidCheckRunMutationVariables) {
      try {
        const response = await $api.delete('/check-runs', {
          headers: {
            'Content-Type': 'application/json'
          },
          data: variables
        })

        if (response.status === 204) {
          return
        }

        return response.data
      } catch (e) {
        if (isAxiosError(e)) {
          const err = new Error(e.response?.data?.message ?? 'Failed to void check run')

          ;(err as Error & { description?: string }).description = e.response?.data?.description
          throw err
        }
      }
    }
  })

  const restoreCheckRunMutation = useMutation({
    async mutationFn(variables: RestoreCheckRunMutationVariables) {
      try {
        const response = await $api.post(`/check-runs/${variables.id}/restore`)

        if (response.status === 204) {
          return
        }

        return response.data
      } catch (e) {
        if (isAxiosError(e)) {
          const err = new Error(e.response?.data?.message ?? 'Failed to restore check run')

          ;(err as Error & { description?: string }).description = e.response?.data?.description
          throw err
        }
      }
    }
  })

  const refreshCheckRunCacheMutation = useMutation({
    async mutationFn(variables: RefreshCheckRunCacheMutationVariables) {
      try {
        const response = await $api.post(`/check-runs/${variables.id}/refresh-cache`)

        if (response.status === 204) {
          return
        }

        return response.data
      } catch (e) {
        if (isAxiosError(e)) {
          const err = new Error(e.response?.data?.message ?? 'Failed to refresh check run cache')

          ;(err as Error & { description?: string }).description = e.response?.data?.description
          throw err
        }
      }
    }
  })

  const handleRefresh = useCallback(() => {
    void query.refetch()
    void queryClient.invalidateQueries(['check-runs', 'meta', 'totals'])

    // Invalidate check run general information
    void queryClient.invalidateQueries({
      predicate(query) {
        return (
          Array.isArray(query.queryKey) &&
          query.queryKey.length === 2 &&
          query.queryKey[0] === 'check-runs'
        )
      }
    })

    // Invalidate timelines
    void queryClient.invalidateQueries({
      predicate(query) {
        return (
          Array.isArray(query.queryKey) &&
          query.queryKey.length === 3 &&
          query.queryKey[0] === 'check-runs' &&
          query.queryKey[2] === 'events'
        )
      }
    })
  }, [query, queryClient])

  const hasOnDemandCheckRuns = query.data?.data?.some(run => run.type === 'on-demand') ?? false

  const columns = useMemo<ColumnsType<PartialCheckRun>>(
    () => [
      ...(hasOnDemandCheckRuns
        ? [
            {
              align: 'center',
              onCell() {
                return {
                  className: cx(baseCellClassName)
                }
              },
              onHeaderCell() {
                return {
                  className: cx(baseHeaderCellClassName)
                }
              },
              render(_, record) {
                if (record.type === 'on-demand') {
                  return (
                    <Tooltip title='On-demand Check Run'>
                      <ToolOutlined className='text-sm text-gray-500 -translate-y-px' />
                    </Tooltip>
                  )
                }
              }
            } satisfies ColumnsType<PartialCheckRun>[0]
          ]
        : []),
      {
        title: 'Group #',
        dataIndex: ['group', 'number'],
        filterIcon: () => (
          <SearchOutlined className={cx({ 'text-blue-600': !!state.filters.groupNumber })} />
        ),
        filterDropdown: ({ close }) => {
          return (
            <div className='p-2 w-64' onKeyDown={e => e.stopPropagation()}>
              <DebounceSelect
                placeholder='Group #'
                style={{ width: '100%' }}
                value={
                  state.filters.groupNumber
                    ? { value: state.filters.groupNumber, label: state.filters.groupNumber }
                    : undefined
                }
                fetchOptions={(search: string) => Payments.getFilter({ filterLine: search })}
                onChange={opt => {
                  const arr = Array.isArray(opt) ? opt : opt ? [opt] : []
                  const first = arr[0] as { value?: string; label?: string } | undefined
                  const value = first?.value
                  dispatch({
                    type: 'filters/set',
                    payload: {
                      ...state.filters,
                      groupNumber: value || undefined
                    }
                  })
                  dispatch({ type: 'pagination/currentPage/set', payload: 0 })
                  close?.()
                }}
                allowClear
                onClear={() => {
                  dispatch({
                    type: 'filters/set',
                    payload: { ...state.filters, groupNumber: undefined }
                  })
                  dispatch({ type: 'pagination/currentPage/set', payload: 0 })
                }}
              />
            </div>
          )
        },
        onCell() {
          return {
            className: cx(baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(baseHeaderCellClassName)
          }
        }
      },
      {
        title: 'Group Name',
        dataIndex: ['group', 'name'],
        width: '20%',
        filterIcon: () => (
          <SearchOutlined className={cx({ 'text-blue-600': !!state.filters.groupName })} />
        ),
        filterDropdown: ({ close }) => {
          return (
            <div className='p-2 w-64' onKeyDown={e => e.stopPropagation()}>
              <DebounceSelect
                placeholder='Group Name'
                style={{ width: '100%' }}
                value={
                  state.filters.groupName
                    ? { value: state.filters.groupName, label: state.filters.groupName }
                    : undefined
                }
                fetchOptions={(search: string) => Payments.getFilter({ filterLine: search })}
                onChange={opt => {
                  const arr = Array.isArray(opt) ? opt : opt ? [opt] : []
                  const first = arr[0] as { value?: string; label?: string } | undefined
                  const rawLabel = first?.label ?? ''
                  const value = first?.value
                  // label format: "<Group#> | <Group Name>"; extract name part safely
                  const parts = rawLabel.split('|')
                  const extracted = parts.length > 1 ? (parts[1]?.trim() ?? '') : rawLabel.trim()
                  const name = extracted || undefined
                  dispatch({
                    type: 'filters/set',
                    payload: {
                      ...state.filters,
                      groupNumber: value || undefined,
                      groupName: name || undefined
                    }
                  })
                  dispatch({ type: 'pagination/currentPage/set', payload: 0 })
                  close?.()
                }}
                allowClear
                onClear={() => {
                  dispatch({
                    type: 'filters/set',
                    payload: { ...state.filters, groupNumber: undefined, groupName: undefined }
                  })
                  dispatch({ type: 'pagination/currentPage/set', payload: 0 })
                }}
              />
            </div>
          )
        },
        onCell() {
          return {
            className: cx(baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(baseHeaderCellClassName)
          }
        }
      },
      {
        title: 'Location',
        key: 'location',
        dataIndex: ['location', 'short_name'],
        align: 'center',

        filters: (filtersQuery.data?.location ?? []).map(location => ({
          text: (
            <div className='flex flex-row justify-between items-center w-full min-w-[10rem]'>
              <span>
                {location.name}

                {location.name !== location.short_name && (
                  <>
                    &nbsp;
                    <span className='text-gray-500'>({location.short_name})</span>
                  </>
                )}
              </span>

              <span
                className={cx({
                  'text-gray-500': location.count > 0,
                  'text-gray-300': location.count === 0
                })}
              >
                {location.count}
              </span>
            </div>
          ),
          value: location.id
        })),

        filterMultiple: true,
        filteredValue: state.filters.locationIds,

        onCell() {
          return {
            className: cx(baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(baseHeaderCellClassName)
          }
        }
      },
      {
        title: 'File Name',
        dataIndex: 'file_names',
        align: 'center',
        render(value: string[] | null) {
          if (!(value?.length ?? 0)) {
            return <EmptyCellValue />
          }

          return (
            <div className='flex flex-col gap-1'>
              {(value ?? []).map(name => (
                <span key={name}>{name}</span>
              ))}
            </div>
          )
        },
        onCell() {
          return {
            className: cx(baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(baseHeaderCellClassName)
          }
        }
      },
      {
        title: 'File Date',
        key: 'date',
        dataIndex: 'date',
        align: 'center',

        filterDropdown: DateFilterDropdown,

        filteredValue:
          (typeof state.filters.date.from === 'string' && state.filters.date.from.length > 0) ||
          (typeof state.filters.date.to === 'string' && state.filters.date.to.length > 0)
            ? [state.filters.date.from ?? '', state.filters.date.to ?? '']
            : [],

        render(value: string) {
          return format(parseISO(value), 'MM/dd/yyyy')
        },
        onCell() {
          return {
            className: cx(baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(baseHeaderCellClassName)
          }
        }
      },
      {
        title: 'Amount',
        align: 'right',
        dataIndex: 'amount',
        render(value: number | null, record) {
          return record.has_data && typeof value === 'number' ? (
            currencyFormatter.format(value)
          ) : (
            <EmptyCellValue />
          )
        },
        onCell() {
          return {
            className: cx(baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(baseHeaderCellClassName)
          }
        },

        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => {
          const values = (selectedKeys ?? []) as string[]

          const inputValue = values.find(value => value.startsWith('cn:'))?.slice(3)

          const isEqualZeroChecked = values.some(value => value === 'eq:0')
          const isGreaterThanZeroChecked = values.some(value => value === 'gt:0')
          const isEqualEmptyChecked = values.some(value => value === 'nl:0')

          const handleChange = ({
            input,
            zero,
            greaterZero,
            empty
          }: {
            input: string | undefined
            zero: boolean
            greaterZero: boolean
            empty: boolean
          }) => {
            const newValue: string[] = []

            if (input !== undefined) {
              newValue.push(`cn:${input}`)
            }

            if (zero) {
              newValue.push('eq:0')
            }

            if (greaterZero) {
              newValue.push('gt:0')
            }

            if (empty) {
              newValue.push('nl:0')
            }

            setSelectedKeys(newValue.length > 0 ? newValue : [])
          }

          return (
            <div className='flex flex-col gap-2 py-2 px-3' style={{ minWidth: 180 }}>
              <InputNumber
                prefix='$'
                min={0}
                className='w-full'
                placeholder='Amount'
                value={inputValue ? Number(inputValue) : undefined}
                onChange={value =>
                  handleChange({
                    input: value !== null ? String(value) : undefined,
                    zero: isEqualZeroChecked,
                    greaterZero: isGreaterThanZeroChecked,
                    empty: isEqualEmptyChecked
                  })
                }
              />

              <div className='flex flex-col gap-1 pt-1'>
                <Checkbox
                  checked={isEqualZeroChecked}
                  onChange={event =>
                    handleChange({
                      input: inputValue,
                      zero: event.target.checked,
                      greaterZero: isGreaterThanZeroChecked,
                      empty: isEqualEmptyChecked
                    })
                  }
                >
                  $0.00
                </Checkbox>

                <Checkbox
                  checked={isGreaterThanZeroChecked}
                  onChange={event =>
                    handleChange({
                      input: inputValue,
                      zero: isEqualZeroChecked,
                      greaterZero: event.target.checked,
                      empty: isEqualEmptyChecked
                    })
                  }
                >
                  {'> $0.00'}
                </Checkbox>

                <Checkbox
                  className='text-gray-500'
                  checked={isEqualEmptyChecked}
                  onChange={event =>
                    handleChange({
                      input: inputValue,
                      zero: isEqualZeroChecked,
                      greaterZero: isGreaterThanZeroChecked,
                      empty: event.target.checked
                    })
                  }
                >
                  (Empty)
                </Checkbox>
              </div>

              <div className='flex justify-between items-center pt-2 border-t border-t-gray-100'>
                <Button
                  onClick={() => {
                    setSelectedKeys([])
                    confirm()
                  }}
                  size='small'
                  type='text'
                  disabled={values.length === 0}
                >
                  Reset
                </Button>

                <Button type='primary' size='small' onClick={() => confirm()}>
                  Apply
                </Button>
              </div>
            </div>
          )
        },

        filteredValue: state.filters.amount.length > 0 ? state.filters.amount : undefined
      },
      {
        title: 'Release Type',
        key: 'releaseType',
        dataIndex: 'releaseType',
        align: 'center',

        filters: releaseTypeFilterOptions.map(option => {
          const count = filtersQuery.data?.releaseType?.find(
            type =>
              type.release_type === option.releaseType && type.release_day === option.releaseDay
          )?.count

          return {
            text: (
              <div className='min-w-[10rem] flex justify-between'>
                <span>{option.label}</span>

                {typeof count === 'number' && <span className='pr-3 text-gray-400'>({count})</span>}
              </div>
            ),
            value:
              option.label === 'Auto'
                ? (undefined as unknown as string)
                : [option.releaseType, option.releaseDay].filter(Boolean).join(':'),

            children: option.children?.map(child => {
              const count =
                filtersQuery.data?.releaseType?.find(
                  type =>
                    type.release_type === child.releaseType && type.release_day === child.releaseDay
                )?.count ?? 0

              return {
                text: (
                  <div className='min-w-[10rem] flex justify-between'>
                    <span>{child.label}</span>

                    {typeof count === 'number' && (
                      <span className='text-gray-400 pr-[26px]'>({count})</span>
                    )}
                  </div>
                ),
                value: [child.releaseType, child.releaseDay].filter(Boolean).join(':')
              }
            })
          }
        }),

        filterMultiple: true,
        filterMode: 'tree',
        filteredValue: state.filters.releaseType,

        render(_, record) {
          if (typeof record.group.release_type !== 'string') {
            return <EmptyCellValue />
          }

          const releaseType =
            releaseTypeMapper[record.group.release_type] ?? record.group.release_type
          const releaseDay = weekDays[record.group.release_day ?? -1]

          return (
            <span>
              <span className='text-lucent-text'>{releaseType}</span>

              {record.group.release_type === 'auto' && typeof releaseDay === 'string' && (
                <span className='text-lucent-gray'> ({releaseDay.slice(0, 3)})</span>
              )}
            </span>
          )
        },
        onCell() {
          return {
            className: cx(baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(baseHeaderCellClassName)
          }
        }
      },
      {
        title: 'Scheduled Release',
        dataIndex: 'next_release_date',
        key: 'scheduledReleaseDate',
        align: 'center',

        filterDropdown: DateFilterDropdown,

        filteredValue:
          (typeof state.filters.scheduledReleaseDate.from === 'string' &&
            state.filters.scheduledReleaseDate.from.length > 0) ||
          (typeof state.filters.scheduledReleaseDate.to === 'string' &&
            state.filters.scheduledReleaseDate.to.length > 0)
            ? [
                state.filters.scheduledReleaseDate.from ?? '',
                state.filters.scheduledReleaseDate.to ?? ''
              ]
            : [],

        render(value: string | null, record) {
          const fits =
            Array.isArray(record.status_ids) &&
            record.status_ids.length === 1 &&
            record.status_ids[0] === '5'

          if (!fits) {
            return <EmptyCellValue />
          }

          switch (record.group.release_type) {
            case 'email': {
              return (
                <Tag color='blue' className='font-lato size-xs'>
                  Waiting for Approval
                </Tag>
              )
            }

            case 'deposit':
            case 'level-funded': {
              return (
                <Tag color='blue' className='font-lato size-xs'>
                  Waiting for Deposit
                </Tag>
              )
            }

            default: {
              if (typeof value !== 'string') {
                return <EmptyCellValue />
              }

              return (
                <Tooltip title={format(parseISO(value), 'MM/dd/yyyy hh:mm a')}>
                  {format(parseISO(value), 'MM/dd/yyyy')}
                </Tooltip>
              )
            }
          }
        },
        onCell() {
          return {
            className: cx(baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(baseHeaderCellClassName)
          }
        }
      },
      {
        title: 'Status',
        dataIndex: 'status_ids',
        key: 'status',
        align: 'center',

        filters: statusGroups
          .map(group => {
            return {
              text: <span className={cx('font-semibold', group.className)}>{group.name}</span>,
              value: group.value,
              children: group.statusIds.map(id => ({
                text: (
                  <Tag
                    color={statusMapper[id]?.color ?? 'default'}
                    title={
                      Array.isArray(filtersQuery.data?.status)
                        ? (filtersQuery.data?.status.find(status => status.id === id)?.name ??
                          statusMapper[id]?.name)
                        : statusMapper[id]?.name
                    }
                    className='font-lato size-xs'
                  >
                    {statusMapper[id]?.name}
                  </Tag>
                ),
                value: id
              }))
            }
          })
          .concat(
            ['mixed', 'no-payment-file', 'deleted'].map(value => {
              return {
                text: (
                  <Tag
                    color={statusMapper[value]?.color ?? 'default'}
                    title={statusMapper[value]?.name}
                    className='font-lato size-xs'
                  >
                    {statusMapper[value]?.name}
                  </Tag>
                ),
                value,
                children: []
              }
            })
          ),

        filterMode: 'tree',
        filterMultiple: true,
        filteredValue: state.filters.statusIds,

        render(value: string[] | null, record) {
          if (record.deleted_at) {
            return (
              <Tag
                color='orange'
                title={`Deleted at ${format(parseISO(record.deleted_at), 'MM/dd/yyyy hh:mm a')}`}
                className='font-lato size-xs'
              >
                Deleted
              </Tag>
            )
          }

          if (!record.has_data) {
            return (
              <Tag color='default' className='font-lato size-xs'>
                Scheduled
              </Tag>
            )
          }

          if (!Array.isArray(value) || value.length === 0) {
            return <EmptyCellValue />
          }

          if (value.length === 1) {
            const config = statusMapper[String(value[0])]
            const tooltipTitle = record.error_details || record.warning

            return (
              <Tooltip title={tooltipTitle}>
                <Tag color={config?.color ?? 'default'} className='font-lato size-xs'>
                  {config?.name}
                </Tag>
              </Tooltip>
            )
          }

          return (
            <Tag color='orange' className='font-lato size-xs'>
              Mixed
            </Tag>
          )
        },
        onCell() {
          return {
            className: cx(baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(baseHeaderCellClassName)
          }
        }
      },
      {
        title: 'Flags',
        key: 'flags',
        dataIndex: 'flags',
        align: 'center',

        filters: [
          {
            value: 'on-hold',
            text: 'On Hold'
          },
          {
            value: 'stop-loss',
            text: 'Stop Loss'
          },
          {
            value: 'totals-mismatch',
            text: 'Totals Mismatch'
          },
          {
            value: 'has-voided-claims',
            text: 'Has Voided Claims'
          },
          {
            value: 'claim-duplicates',
            text: 'Claim Duplicates'
          },
          {
            value: 'network',
            text: 'Network'
          },
          {
            value: 'has-deleted-files',
            text: 'Has Deleted Files'
          }
        ],

        filterMultiple: true,
        filteredValue: state.filters.flags,

        render(value: string[] | null) {
          return (
            <div className='flex gap-2 justify-center items-center px-4'>
              {value?.includes('on-hold') && (
                <Tooltip title='On Hold'>
                  <PauseCircleFilled className='text-sm text-yellow-500' />
                </Tooltip>
              )}

              {value?.includes('stop-loss') && (
                <Tooltip title='Stop Loss'>
                  <FlagFilled className='text-sm text-lucent-red' />
                </Tooltip>
              )}

              {value?.includes('claim-duplicates') && (
                <Tooltip title='Claim Duplicates'>
                  <Duplicate />
                </Tooltip>
              )}

              {value?.includes('totals-mismatch') && (
                <Tooltip title='Totals Mismatch'>
                  <DollarCircleOutlined className='text-sm text-lucent-red' />
                </Tooltip>
              )}

              {value?.includes('has-voided-claims') && (
                <Tooltip title='Has Voided Claims'>
                  <FireOutlined className='text-sm text-cyan-600' />
                </Tooltip>
              )}

              {value?.includes('network') && (
                <Tooltip title='Network Claims'>
                  <FireOutlined className='text-sm text-cyan-600' />
                </Tooltip>
              )}

              {value?.includes('has-deleted-files') && (
                <Tooltip title='Has Deleted Files'>
                  <DeleteFilled className='text-sm text-orange-500' />
                </Tooltip>
              )}
            </div>
          )
        },
        onCell() {
          return {
            className: cx(baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(baseHeaderCellClassName)
          }
        }
      },
      {
        render(_, record) {
          const uniqueStatusIds = record.status_ids?.slice().sort().filter(uniquePredicate) ?? []
          const successStatuses = statusGroups.find(g => g.value === 'success')?.statusIds ?? []

          const canBeVoided =
            record.has_data &&
            round(record.amount ?? 0, 2) === 0 &&
            uniqueStatusIds.every(id => !successStatuses.includes(id)) &&
            !record.deleted_at

          const canBeRestored = record.deleted_at !== null && has([Role.Administrator])

          const canDropoff = uniqueStatusIds.length === 1 && uniqueStatusIds.includes('5')

          return (
            <Dropdown
              menu={{
                items: [
                  {
                    key: 'rerun',
                    label: 'Rerun',
                    icon: <HistoryOutlined />
                  },
                  ...(canBeVoided
                    ? [
                        {
                          key: 'void',
                          label: 'Void',
                          icon: <FireOutlined />
                        }
                      ]
                    : []),
                  ...(canBeRestored
                    ? [
                        {
                          key: 'restore',
                          label: 'Restore',
                          icon: <RollbackOutlined />
                        }
                      ]
                    : []),
                  {
                    key: 'refresh-cache',
                    label: 'Refresh cache',
                    icon: <ReloadOutlined />
                  },
                  {
                    key: 'scheduler',
                    label: 'Find group in Scheduler',
                    icon: <CalendarOutlined />
                  },
                  ...(canDropoff
                    ? [
                        {
                          key: 'last-report',
                          label: 'Last Reports',
                          icon: <FileExcelOutlined />,
                          children: [
                            {
                              key: 'dropoff',
                              label: (
                                <span>
                                  Dropoff to <span className='font-mono'>LIN</span>
                                </span>
                              ),
                              icon: <CloudUploadOutlined />
                            }
                          ]
                        }
                      ]
                    : []),
                  ...(typeof record.synced_at === 'string'
                    ? [
                        {
                          key: 'info',
                          label: 'Info',
                          icon: <InfoCircleOutlined />,
                          children: [
                            {
                              key: 'synced-at',
                              label: (
                                <span>
                                  Synced <HumanReadableTime value={new Date(record.synced_at)} />
                                </span>
                              ),
                              icon: <ClockCircleOutlined />
                            }
                          ]
                        }
                      ]
                    : [])
                ],
                onClick(info) {
                  info.domEvent.preventDefault()
                  info.domEvent.stopPropagation()

                  switch (info.key) {
                    case 'rerun': {
                      dispatch({
                        type: 'rerunCheckRunModal/open',
                        payload: [record.id]
                      })

                      break
                    }

                    case 'void': {
                      const modal = Modal.confirm({
                        title: 'Void',
                        content: (
                          <p>
                            Are you sure you want to void the check run for{' '}
                            <strong>{record.group.number}</strong> on{' '}
                            <strong>
                              {new Date(record.date).toLocaleDateString('en-US', {
                                month: '2-digit',
                                day: '2-digit',
                                year: 'numeric',
                                timeZone: 'UTC'
                              })}
                            </strong>{' '}
                            with <strong>{currencyFormatter.format(record.amount ?? 0)}</strong>{' '}
                            amount?
                          </p>
                        ),
                        icon: <FireOutlined />,

                        async onOk() {
                          return voidCheckRunMutation.mutateAsync(
                            {
                              ids: [record.id]
                            },
                            {
                              onSuccess() {
                                notification.success({
                                  message: 'Void successful',
                                  description: 'The check run has been successfully voided'
                                })

                                void query.refetch()
                                modal.destroy()
                              },

                              onError(error) {
                                const err = error as Error & { description?: string }
                                notification.error({
                                  message: err.message,
                                  description: err.description
                                })
                              }
                            }
                          )
                        },

                        okText: voidCheckRunMutation.isPending ? 'Voiding...' : 'Void'
                      })

                      break
                    }

                    case 'restore': {
                      const modal = Modal.confirm({
                        title: 'Restore',
                        content: (
                          <p>
                            Are you sure you want to restore the check run for{' '}
                            <strong>{record.group.number}</strong> on{' '}
                            <strong>
                              {new Date(record.date).toLocaleDateString('en-US', {
                                month: '2-digit',
                                day: '2-digit',
                                year: 'numeric',
                                timeZone: 'UTC'
                              })}
                            </strong>
                            ?
                          </p>
                        ),
                        icon: <RollbackOutlined />,

                        async onOk() {
                          return restoreCheckRunMutation.mutateAsync(
                            {
                              id: record.id
                            },
                            {
                              onSuccess() {
                                notification.success({
                                  message: 'Restore successful',
                                  description: 'The check run has been successfully restored'
                                })

                                void query.refetch()
                                modal.destroy()
                              },

                              onError(error) {
                                const err = error as Error & { description?: string }
                                notification.error({
                                  message: err.message,
                                  description: err.description
                                })
                              }
                            }
                          )
                        },

                        okText: restoreCheckRunMutation.isPending ? 'Restoring...' : 'Restore'
                      })

                      break
                    }

                    case 'refresh-cache': {
                      refreshCheckRunCacheMutation.mutate(
                        { id: record.id },
                        {
                          onSuccess() {
                            notification.success({
                              message: 'Cache refreshed',
                              description: `The ${format(parseISO(record.date), 'MM/dd/yyyy')} ${record.group.number} check run cache has been successfully refreshed`
                            })
                          },

                          onError(error) {
                            const err = error as Error & { description?: string }
                            notification.error({
                              message: err.message,
                              description: err.description
                            })
                          },

                          async onSettled() {
                            await queryClient.invalidateQueries({
                              queryKey: ['check-runs', record.id]
                            })

                            await queryClient.invalidateQueries({
                              queryKey: ['check-runs', 'meta', 'totals']
                            })

                            await queryClient.invalidateQueries({
                              queryKey: ['check-run-totals']
                            })

                            await queryClient.invalidateQueries({
                              predicate(query) {
                                return (
                                  (Array.isArray(query.queryKey) &&
                                    query.queryKey[0] === 'release-payment-files' &&
                                    ((query.state.data as { data?: PartialCheckRun[] })?.data?.some(
                                      (run: PartialCheckRun) => run.id === record.id
                                    ) ??
                                      false)) ??
                                  false
                                )
                              }
                            })
                          }
                        }
                      )

                      break
                    }

                    case 'scheduler': {
                      navigate(`/groups/scheduler?query=${encodeURIComponent(record.group.number)}`)
                      break
                    }

                    case 'dropoff': {
                      dispatch({
                        type: 'dropoffModal/open',
                        payload: {
                          id: record.id
                        }
                      })
                    }
                  }
                }
              }}
            >
              <Button
                className='flex justify-center items-center'
                icon={<EllipsisOutlined />}
                type='text'
                shape='circle'
                size='small'
              />
            </Dropdown>
          )
        },
        onCell() {
          return {
            className: cx(baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(baseHeaderCellClassName)
          }
        }
      }
    ],
    [
      filtersQuery.data,
      state.filters,
      navigate,
      voidCheckRunMutation,
      query,
      hasOnDemandCheckRuns,
      queryClient,
      refreshCheckRunCacheMutation,
      has,
      restoreCheckRunMutation
    ]
  )

  const isDefaultFilters = useMemo(() => {
    return (
      state.filters.statusIds.length === 0 &&
      state.filters.locationIds.length === 0 &&
      !state.filters.date.from &&
      !state.filters.date.to &&
      !state.filters.scheduledReleaseDate.from &&
      !state.filters.scheduledReleaseDate.to &&
      state.filters.flags.length === 0 &&
      state.filters.releaseType.length === 0 &&
      state.searchQuery.length === 0 &&
      state.filters.amount.length === 0
    )
  }, [state.filters, state.searchQuery])

  useInterval(() => {
    handleRefresh()
  }, state.refreshInterval ?? null)

  const eobsSyncStatusQuery = useQuery({
    queryKey: ['eobs-sync-status'],

    async queryFn({ signal }) {
      const response = await $api.get<EobMetaResponse>('/check-runs/meta/eobs', {
        signal
      })

      const data = response.data

      return data
    },

    refetchInterval: 5_000
  })

  const selectedCheckRuns =
    query.data?.data?.filter(run => state.selectedCheckRunIds.includes(run.id)) ?? []
  const deleteableCheckRuns = selectedCheckRuns.filter(run => !run.has_data && !run.deleted_at)

  const deleteCheckRunsMutation = useMutation({
    async mutationFn(variables: DeleteCheckRunsMutationVariables) {
      try {
        await $api.delete('/check-runs', {
          headers: {
            'Content-Type': 'application/json'
          },
          data: variables
        })
      } catch {
        throw new Error('Something went wrong')
      }
    }
  })

  const batchSelectionContextValue = useMemo<BatchSelectionContextValue>(() => {
    return {
      selectedCheckRunIds: state.selectedCheckRunIds,
      selectedPaymentFileIds: state.selectedPaymentFileIds,
      selectedEOBIds: state.selectedEOBIds,
      selectedClaimIds: state.selectedClaimIds,

      onSelectCheckRuns(ids) {
        dispatch({
          type: 'checkRunIds/select',
          payload: ids
        })
      },

      onSelectPaymentFiles(ids) {
        dispatch({
          type: 'paymentFileIds/select',
          payload: ids
        })
      },

      onSelectEOBs(ids) {
        dispatch({
          type: 'eobIds/select',
          payload: ids
        })
      },

      onSelectClaims(ids) {
        dispatch({
          type: 'claimIds/select',
          payload: ids
        })
      }
    }
  }, [
    state.selectedCheckRunIds,
    state.selectedPaymentFileIds,
    state.selectedEOBIds,
    state.selectedClaimIds
  ])

  const builtStatusIds = statusGroups.find(g => g.value === 'success')?.statusIds ?? []
  const waitingStatusIds = [
    ...(statusGroups.find(g => g.value === 'pending')?.statusIds ?? []),
    'no-payment-file'
  ]
  const voidedStatusIds = ['8', 'deleted']
  const noPaymentFileStatusIds = ['no-payment-file']
  const pendingStatusIds = statusGroups.find(g => g.value === 'pending')?.statusIds ?? []
  const failedStatusIds = statusGroups.find(g => g.value === 'error')?.statusIds ?? []

  const scheduledStatusIds = [...builtStatusIds, ...waitingStatusIds, ...voidedStatusIds]

  const isDefaultFiltersBase =
    state.filters.locationIds.length === 0 &&
    !state.filters.scheduledReleaseDate.from &&
    !state.filters.scheduledReleaseDate.to &&
    state.filters.flags.length === 0 &&
    state.filters.releaseType.length === 0

  const isTodaySelected =
    state.filters.date.from === formatISO(new Date(), { representation: 'date' }) &&
    state.filters.date.to === formatISO(new Date(), { representation: 'date' })

  const isNoDateSelected = !state.filters.date.from && !state.filters.date.to

  const scheduledIsActive =
    isDefaultFiltersBase &&
    isTodaySelected &&
    state.filters.statusIds.length === scheduledStatusIds.length &&
    state.filters.statusIds.every(id => scheduledStatusIds.includes(id)) &&
    scheduledStatusIds.every(id => state.filters.statusIds.includes(id))

  const builtIsActive =
    isDefaultFiltersBase &&
    isTodaySelected &&
    state.filters.statusIds.length === builtStatusIds.length &&
    state.filters.statusIds.every(id => builtStatusIds.includes(id)) &&
    builtStatusIds.every(id => state.filters.statusIds.includes(id))

  const waitingIsActive =
    isDefaultFiltersBase &&
    isTodaySelected &&
    state.filters.statusIds.length === waitingStatusIds.length &&
    state.filters.statusIds.every(id => waitingStatusIds.includes(id)) &&
    waitingStatusIds.every(id => state.filters.statusIds.includes(id))

  const voidedIsActive =
    isDefaultFiltersBase &&
    isTodaySelected &&
    state.filters.statusIds.length === voidedStatusIds.length &&
    state.filters.statusIds.every(id => voidedStatusIds.includes(id)) &&
    voidedStatusIds.every(id => state.filters.statusIds.includes(id))

  const noPaymentFileIsActive =
    isDefaultFiltersBase &&
    isNoDateSelected &&
    state.filters.statusIds.length === noPaymentFileStatusIds.length &&
    state.filters.statusIds.every(id => noPaymentFileStatusIds.includes(id)) &&
    noPaymentFileStatusIds.every(id => state.filters.statusIds.includes(id))

  const pendingIsActive =
    isDefaultFiltersBase &&
    isNoDateSelected &&
    state.filters.statusIds.length === pendingStatusIds.length &&
    state.filters.statusIds.every(id => pendingStatusIds.includes(id)) &&
    pendingStatusIds.every(id => state.filters.statusIds.includes(id))

  const failedIsActive =
    isDefaultFiltersBase &&
    isNoDateSelected &&
    state.filters.statusIds.length === failedStatusIds.length &&
    state.filters.statusIds.every(id => failedStatusIds.includes(id)) &&
    failedStatusIds.every(id => state.filters.statusIds.includes(id))

  const nomiIsActive = state.searchQuery.toLowerCase().trim() === 'nomi'

  function getFiltersFromUrlParams(searchParams: URLSearchParams): {
    filters: State['filters']
    searchQuery: string
    pagination: State['pagination']
  } {
    const filters: State['filters'] = {
      statusIds: [],
      locationIds: [],
      date: {},
      scheduledReleaseDate: {},
      flags: [],
      releaseType: [],
      groupNumber: undefined,
      groupName: undefined,
      amount: []
    }

    // Get status IDs
    const statusIds = searchParams.get('statusIds')
    if (statusIds) {
      filters.statusIds = statusIds.split(',').filter(id => validStatusIds.has(id))
    }

    // Get location IDs
    const locationIds = searchParams.get('locationIds')
    if (locationIds) {
      filters.locationIds = locationIds.split(',')
    }

    // Get date filters
    const dateFrom = searchParams.get('dateFrom')
    if (dateFrom) {
      filters.date.from = dateFrom
    }
    const dateTo = searchParams.get('dateTo')
    if (dateTo) {
      filters.date.to = dateTo
    }

    // Get scheduled release date filters
    const scheduledDateFrom = searchParams.get('scheduledDateFrom')
    if (scheduledDateFrom) {
      filters.scheduledReleaseDate.from = scheduledDateFrom
    }
    const scheduledDateTo = searchParams.get('scheduledDateTo')
    if (scheduledDateTo) {
      filters.scheduledReleaseDate.to = scheduledDateTo
    }

    // Get flags
    const flags = searchParams.get('flags')
    if (flags) {
      filters.flags = flags.split(',')
    }

    // Get release type
    const releaseType = searchParams.get('releaseType')
    if (releaseType) {
      filters.releaseType = releaseType.split(',')
    }

    // Get exact group filters (from URL params)
    const groupNumber = searchParams.get('groupNumber')
    if (groupNumber) {
      filters.groupNumber = groupNumber
    }
    const groupName = searchParams.get('groupName')
    if (groupName) {
      filters.groupName = groupName
    }

    const amount = searchParams.getAll('amount')
    if (amount.length > 0) {
      filters.amount = amount
    }

    // Get search query
    const searchQuery = searchParams.get('search') ?? ''

    // Get pagination
    const pagination: State['pagination'] = {
      currentPage: Number(searchParams.get('page')) || 0,
      pageSize: Number(searchParams.get('pageSize')) || 10
    }

    return { filters, searchQuery, pagination }
  }

  // Initialize filters from URL when component mounts
  useEffect(() => {
    const { filters, searchQuery, pagination } = getFiltersFromUrlParams(searchParams)

    // Only update state if there are actual filters in the URL
    if (
      filters.statusIds.length > 0 ||
      filters.locationIds.length > 0 ||
      filters.date.from != null ||
      filters.date.to != null ||
      filters.scheduledReleaseDate.from != null ||
      filters.scheduledReleaseDate.to != null ||
      filters.flags.length > 0 ||
      filters.releaseType.length > 0 ||
      filters.amount.length > 0 ||
      searchQuery !== '' ||
      pagination.currentPage > 0 ||
      pagination.pageSize !== 10
    ) {
      dispatch({
        type: 'filters/set',
        payload: filters
      })

      if (searchQuery) {
        dispatch({
          type: 'searchQuery/set',
          payload: searchQuery
        })
      }

      dispatch({
        type: 'pagination/currentPage/set',
        payload: pagination.currentPage
      })

      dispatch({
        type: 'pagination/pageSize/set',
        payload: pagination.pageSize
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only run once on mount - getFiltersFromUrlParams and searchParams intentionally excluded

  // Update URL search params when filters change
  useEffect(() => {
    const params = syncFiltersToUrlParams(state.filters, state.searchQuery, state.pagination)
    setSearchParams(params)
  }, [state.filters, state.searchQuery, state.pagination, setSearchParams])

  const handleUploadFileButtonClick = () => {
    setIsFilePickerOpen(isOpen => !isOpen)
  }

  return (
    <HelpDrawerLayout title='Release Payment Files' sections={releasePaymentFilesHelpSections}>
      <PageContainer className='space-y-4'>
        <div className='flex flex-row flex-wrap justify-between items-start gap-4'>
          <PageHeader
            className='[&_h3]:whitespace-normal sm:[&_h3]:whitespace-nowrap'
            suffix={<HelpDrawerTrigger />}
          />

          <MiniDashboard
            scheduledIsActive={scheduledIsActive}
            onScheduledClick={() => {
              dispatch({
                type: 'filters/set',
                payload: {
                  ...initialState.filters,
                  date: {
                    ...state.filters.date,
                    from: formatISO(new Date(), { representation: 'date' }),
                    to: formatISO(new Date(), { representation: 'date' })
                  },
                  statusIds: scheduledStatusIds
                }
              })
            }}
            builtIsActive={builtIsActive}
            onBuiltClick={() => {
              dispatch({
                type: 'filters/set',
                payload: {
                  ...initialState.filters,
                  date: {
                    ...state.filters.date,
                    from: formatISO(new Date(), { representation: 'date' }),
                    to: formatISO(new Date(), { representation: 'date' })
                  },
                  statusIds: builtStatusIds
                }
              })
            }}
            waitingIsActive={waitingIsActive}
            onWaitingClick={() => {
              dispatch({
                type: 'filters/set',
                payload: {
                  ...initialState.filters,

                  date: {
                    ...state.filters.date,
                    from: formatISO(new Date(), { representation: 'date' }),
                    to: formatISO(new Date(), { representation: 'date' })
                  },
                  statusIds: waitingStatusIds
                }
              })
            }}
            voidedIsActive={voidedIsActive}
            onVoidedClick={() => {
              dispatch({
                type: 'filters/set',
                payload: {
                  ...initialState.filters,
                  date: {
                    ...state.filters.date,
                    from: formatISO(new Date(), { representation: 'date' }),
                    to: formatISO(new Date(), { representation: 'date' })
                  },
                  statusIds: voidedStatusIds
                }
              })
            }}
            noPaymentFileIsActive={noPaymentFileIsActive}
            onNoPaymentFileClick={() => {
              dispatch({
                type: 'filters/set',
                payload: {
                  ...initialState.filters,
                  statusIds: noPaymentFileStatusIds
                }
              })
            }}
            pendingIsActive={pendingIsActive}
            onPendingClick={() => {
              dispatch({
                type: 'filters/set',
                payload: {
                  ...initialState.filters,
                  statusIds: pendingStatusIds
                }
              })
            }}
            failedIsActive={failedIsActive}
            onFailedClick={() => {
              dispatch({
                type: 'filters/set',
                payload: {
                  ...initialState.filters,
                  statusIds: failedStatusIds
                }
              })
            }}
            nomiIsActive={nomiIsActive}
            onNomiClick={() => {
              dispatch({
                type: 'searchQuery/set',
                payload: 'nomi'
              })
            }}
          />
        </div>

        <div className='flex flex-col rounded-lg border min-w-0 overflow-x-auto'>
          <div className='flex flex-col'>
            <div className='flex flex-row flex-wrap gap-2 items-center p-4'>
              <div className='flex-grow min-w-[200px]'>
                <Input
                  placeholder='Search by group #, group name, location, file name, or file date'
                  className='font-lato text-sm py-1 bg-gray-50 [&_input]:bg-transparent rounded [&_input]:placeholder:text-gray-400'
                  prefix={<SearchOutlined className='text-gray-500' />}
                  suffix={
                    state.searchQuery.length > 0 ? (
                      <button
                        type='button'
                        className='flex justify-center items-center p-2 -m-2 border'
                        onClick={() => {
                          dispatch({
                            type: 'searchQuery/set',
                            payload: ''
                          })
                        }}
                      >
                        <CloseOutlined size={16} className='text-gray-500' />
                      </button>
                    ) : undefined
                  }
                  value={state.searchQuery}
                  onChange={event => {
                    dispatch({
                      type: 'searchQuery/set',
                      payload: event.target.value
                    })
                  }}
                />
              </div>

              <Space>
                {!isDefaultFilters && (
                  <Button
                    type='default'
                    onClick={() => {
                      dispatch({ type: 'filters/clear' })
                    }}
                    className='flex items-center py-1'
                  >
                    <DeleteFilled className='text-lucent-primary' />
                    <span className='font-lato font-bold text-lucent-primary text-[10px] uppercase'>
                      Clear Filters
                    </span>
                  </Button>
                )}

                <Dropdown.Button
                  type='default'
                  disabled={query.isFetching}
                  onClick={() => {
                    void query.refetch()
                    void queryClient.invalidateQueries(['check-runs', 'meta', 'totals'])
                  }}
                  icon={
                    <CaretDownOutlined
                      className={cx(
                        'w-[12px] h-[12px] !translate-y-[2px]',
                        typeof state.refreshInterval === 'number' && 'text-lucent-primary',
                        typeof state.refreshInterval !== 'number' && 'text-lucent-gray'
                      )}
                    />
                  }
                  menu={{
                    items: [30, 60, 60 * 5, 60 * 30].map(seconds => ({
                      key: `refresh-${seconds}`,
                      itemIcon() {
                        if (state.refreshInterval === seconds * 1000) {
                          return <CheckOutlined className='ml-2 text-lucent-primary' />
                        }

                        return null
                      },
                      onClick() {
                        if (state.refreshInterval === seconds * 1000) {
                          dispatch({
                            type: 'refreshInterval/clear'
                          })

                          return
                        }

                        dispatch({
                          type: 'refreshInterval/set',
                          payload: seconds * 1000
                        })
                      },
                      label: humanizeDuration(seconds * 1000)
                    }))
                  }}
                >
                  <div className='flex items-center space-x-1.5'>
                    <SyncOutlined
                      className={cx(
                        'text-base',
                        query.isFetching && 'animate-custom-spin text-lucent-gray',
                        !query.isFetching && 'text-lucent-primary'
                      )}
                    />

                    <span
                      className={cx(
                        'font-lato font-bold text-[10px] uppercase',
                        query.isFetching && 'text-lucent-gray',
                        !query.isFetching && 'text-lucent-primary'
                      )}
                    >
                      <span>Refresh</span>

                      {typeof state.refreshInterval === 'number' && (
                        <span>
                          <span className='mx-1.5'>|</span>
                          Auto {humanizeDuration(state.refreshInterval, { short: true })}
                        </span>
                      )}
                    </span>
                  </div>
                </Dropdown.Button>

                <LiveUpdatesControl
                  enabled={state.liveUpdatesEnabled}
                  isConnected={isConnected}
                  onChange={enabled =>
                    dispatch({ type: 'liveUpdatesEnabled/set', payload: enabled })
                  }
                />

                <Button onClick={handleUploadFileButtonClick}>Upload File</Button>
              </Space>
            </div>
          </div>

          <div
            className={cx('px-4 pb-4', {
              hidden: !isFilePickerOpen
            })}
          >
            <FilePicker
              onCancel={() => {
                setIsFilePickerOpen(false)
              }}
            />
          </div>

          <BatchSelectionContext.Provider value={batchSelectionContextValue}>
            <Table
              size='small'
              columns={columns}
              rowKey='id'
              dataSource={query.data?.data ?? []}
              loading={query.isFetching}
              className='rounded-none border-t payment-files-table'
              rowSelection={{
                columnWidth: 2.5 * 16,
                hideSelectAll: false,

                selectedRowKeys: batchSelectionContextValue.selectedCheckRunIds,

                getCheckboxProps() {
                  return {}
                },

                onSelect(_, __, ___, event) {
                  event.stopPropagation()
                },

                onChange(selectedRowKeys) {
                  batchSelectionContextValue.onSelectCheckRuns(selectedRowKeys as string[])
                }
              }}
              components={{
                header: {
                  wrapper(
                    props: JSX.IntrinsicAttributes &
                      ClassAttributes<HTMLTableSectionElement> &
                      HTMLAttributes<HTMLTableSectionElement>
                  ) {
                    return (
                      <thead {...props}>
                        {props.children}

                        <BatchSelectionPanel
                          colSpan={columns.length + 2}
                          selectedItemsCount={batchSelectionContextValue.selectedCheckRunIds.length}
                          pluralization={['check run', 'check runs']}
                          onClearSelection={() => {
                            batchSelectionContextValue.onSelectCheckRuns([])
                          }}
                        >
                          <div className='flex-grow' />

                          {deleteableCheckRuns.length > 0 && (
                            <BatchActionButton
                              onClick={() => {
                                dispatch({
                                  type: 'deleteCheckRunsModal/open',
                                  payload: deleteableCheckRuns.map(run => run.id)
                                })
                              }}
                              title={`${deleteableCheckRuns.length} deleteable ${pluralize(deleteableCheckRuns.length, 'Check Run', 'Check Runs')}`}
                            >
                              Delete
                            </BatchActionButton>
                          )}
                          {has([Role.Administrator]) && (
                            <>
                              <BatchActionButton
                                onClick={() => {
                                  dispatch({
                                    type: 'rerunCheckRunModal/open',
                                    payload: batchSelectionContextValue.selectedCheckRunIds
                                  })
                                }}
                                title='Rerun'
                              >
                                Rerun
                              </BatchActionButton>
                              <BatchActionButton
                                onClick={() => {
                                  const modal = Modal.confirm({
                                    title: 'Void',
                                    content: (
                                      <p>
                                        Are you sure you want to void the check run for{' '}
                                        <strong>
                                          {batchSelectionContextValue.selectedCheckRunIds.length}
                                        </strong>{' '}
                                        check runs?
                                      </p>
                                    ),
                                    icon: <FireOutlined />,

                                    async onOk() {
                                      return voidCheckRunMutation.mutateAsync(
                                        {
                                          ids: batchSelectionContextValue.selectedCheckRunIds
                                        },
                                        {
                                          onSuccess() {
                                            notification.success({
                                              message: 'Void successful',
                                              description:
                                                'The check run has been successfully voided'
                                            })

                                            void query.refetch()
                                            modal.destroy()
                                          },

                                          onError(error) {
                                            const err = error as Error & { description?: string }
                                            notification.error({
                                              message: err.message,
                                              description: err.description
                                            })
                                          }
                                        }
                                      )
                                    },

                                    okText: voidCheckRunMutation.isPending ? 'Voiding...' : 'Void'
                                  })
                                }}
                                title='Void'
                              >
                                Void
                              </BatchActionButton>
                            </>
                          )}
                        </BatchSelectionPanel>
                      </thead>
                    )
                  }
                },
                body: {}
              }}
              expandable={{
                expandedRowKeys: state.expandedRowIds,
                onExpandedRowsChange(expandedRowKeys) {
                  dispatch({
                    type: 'rows/expand',
                    payload: expandedRowKeys.map(key => key.toString(10))
                  })
                },
                expandIcon: ExpandIcon as RenderExpandIcon<PartialCheckRun>,
                showExpandColumn: false,
                expandRowByClick: true,
                expandedRowRender(record) {
                  return <CheckRunExpandableRow key={record.id} {...record} />
                }
              }}
              pagination={{
                current: state.pagination.currentPage + 1,
                pageSize: state.pagination.pageSize,
                pageSizeOptions: ['10', '25', '50', '100'],
                showSizeChanger: true,
                total: query.data?.count ?? 0,
                showTotal(total, [from, to]) {
                  const parts = [
                    `Showing ${from}–${to} of ${total} ${total === 1 ? 'item' : 'items'}`
                  ]

                  if (typeof query.data?.total === 'number' && query.data.total !== total) {
                    parts.push(
                      `Total ${query.data.total} ${query.data.total === 1 ? 'item' : 'items'}`
                    )
                  }

                  if (
                    eobsSyncStatusQuery.data &&
                    eobsSyncStatusQuery.data.code !== 'OK' &&
                    typeof eobsSyncStatusQuery.data.message === 'string'
                  ) {
                    parts.push(eobsSyncStatusQuery.data.message)
                  }

                  return (
                    <span className='text-sm text-gray-500 font-lato'>{parts.join(' • ')}</span>
                  )
                },
                hideOnSinglePage: false
              }}
              onChange={(pagination, filters, _, extra) => {
                if (extra.action === 'filter') {
                  dispatch({
                    type: 'filters/set',
                    payload: {
                      statusIds: (filters.status ?? []) as string[],
                      locationIds: (filters.location ?? []) as string[],
                      date: {
                        from: filters.date?.[0] as string | undefined,
                        to: filters.date?.[1] as string | undefined
                      },
                      scheduledReleaseDate: {
                        from: filters.scheduledReleaseDate?.[0] as string | undefined,
                        to: filters.scheduledReleaseDate?.[1] as string | undefined
                      },
                      flags: (filters.flags ?? []) as string[],
                      releaseType: (filters.releaseType ?? []) as string[],
                      amount: (filters.amount ?? []) as string[]
                    }
                  })
                }

                if (extra.action === 'paginate') {
                  dispatch({
                    type: 'pagination/currentPage/set',
                    payload: (pagination.current ?? 1) - 1
                  })

                  if ((pagination.pageSize ?? 10) !== state.pagination.pageSize) {
                    dispatch({
                      type: 'pagination/pageSize/set',
                      payload: pagination.pageSize ?? 10
                    })
                  }
                }
              }}
            />
          </BatchSelectionContext.Provider>
        </div>

        <Modal
          title='Release Settings'
          open={state.releaseSettingsModal.isOpen}
          onOk={() => {
            dispatch({
              type: 'releaseSettingsModal/submit'
            })

            setTimeout(() => {
              dispatch({
                type: 'releaseSettingsModal/submit/success'
              })
            }, 1000)
          }}
          closable={false}
          confirmLoading={state.releaseSettingsModal.isSubmitting}
          onCancel={() => {
            dispatch({
              type: 'releaseSettingsModal/close'
            })
          }}
        >
          <p>Release settings will be here.</p>
        </Modal>

        <Modal
          title={`${deleteableCheckRuns.length} ${pluralize(deleteableCheckRuns.length, 'Check Run', 'Check Runs')} to Delete`}
          open={state.deleteCheckRunsModal.isOpen}
          okText={
            state.deleteCheckRunsModal.isSubmitting
              ? 'Deleting...'
              : state.deleteCheckRunsModal.error
                ? 'Retry'
                : 'Delete'
          }
          width={800}
          onOk={() => {
            const startedAt = Date.now()
            const minWaitTime = 2000

            dispatch({
              type: 'deleteCheckRunsModal/submit'
            })

            deleteCheckRunsMutation.mutate(
              {
                ids: state.deleteCheckRunsModal.checkRunIds
              },
              {
                onSuccess() {
                  setTimeout(
                    () => {
                      dispatch({
                        type: 'deleteCheckRunsModal/submit/success'
                      })

                      void query.refetch()
                      void queryClient.invalidateQueries(['check-runs', 'meta', 'totals'])
                    },
                    Math.max(minWaitTime - (Date.now() - startedAt), 0)
                  )
                },
                onError(error) {
                  dispatch({
                    type: 'deleteCheckRunsModal/submit/error',
                    payload: error instanceof Error ? error : new Error('Something went wrong')
                  })
                }
              }
            )
          }}
          closable={false}
          confirmLoading={state.deleteCheckRunsModal.isSubmitting}
          onCancel={() => {
            dispatch({
              type: 'deleteCheckRunsModal/close'
            })
          }}
        >
          <div className='flex flex-col gap-4 pb-4'>
            {state.deleteCheckRunsModal.error && (
              <Alert
                type='error'
                message={state.deleteCheckRunsModal.error.message}
                className='mt-2'
                showIcon
              />
            )}

            <p>
              The following{' '}
              {pluralize(deleteableCheckRuns.length, 'check run has', 'check runs have')}{' '}
              <strong>no payment files</strong> and therefore can be <strong>deleted</strong>.
            </p>

            <div className='grid grid-cols-[max-content_max-content_1fr] gap-x-6 gap-y-3'>
              {deleteableCheckRuns.map(run => (
                <Fragment key={run.id}>
                  <span className='font-semibold'>{run.group.number}</span>

                  <span>{run.group.name}</span>

                  <span>
                    {new Date(run.date).toLocaleDateString('en-US', {
                      month: '2-digit',
                      day: '2-digit',
                      year: 'numeric',
                      timeZone: 'UTC'
                    })}
                  </span>
                </Fragment>
              ))}
            </div>

            <p>
              Are you sure you want to <strong>delete</strong>{' '}
              {pluralize(deleteableCheckRuns.length, 'this check run', 'these check runs')}? This
              action cannot be undone.
            </p>
          </div>
        </Modal>

        <RerunCheckRunModal
          checkRunIds={state.rerunCheckRunModal.checkRunIds}
          title='Rerun Check Run'
          open={state.rerunCheckRunModal.isOpen}
          onSuccess={async () => {
            const result = await query.refetch()

            if (result.data?.data?.length === 0 && state.pagination.currentPage > 0) {
              dispatch({
                type: 'pagination/currentPage/set',
                payload: state.pagination.currentPage - 1
              })
            }

            void queryClient.invalidateQueries(['check-runs', 'meta', 'totals'])
            void queryClient.invalidateQueries(['check-runs', state.rerunCheckRunModal.checkRunIds])

            dispatch({
              type: 'rerunCheckRunModal/close'
            })
          }}
          onError={err => {
            dispatch({
              type: 'rerunCheckRunModal/submit/error',
              payload: err instanceof Error ? err : new Error('Something went wrong')
            })

            // TODO: handle error
            dispatch({
              type: 'rerunCheckRunModal/close'
            })
          }}
          onCancel={() => {
            dispatch({
              type: 'rerunCheckRunModal/close'
            })
          }}
        />

        <DropoffModal
          checkRun={state.dropoffModal.checkRun}
          open={state.dropoffModal.isOpen}
          onSuccess={() => {
            dispatch({
              type: 'dropoffModal/close'
            })
          }}
          onError={err => {
            void err

            // TODO: handle error
            dispatch({
              type: 'rerunCheckRunModal/close'
            })
          }}
          onCancel={() => {
            dispatch({
              type: 'dropoffModal/close'
            })
          }}
        />
      </PageContainer>
    </HelpDrawerLayout>
  )
}
